import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import type { MatchedPatientData } from '@store/generated/admin';
import { useSnackbar } from 'notistack';

import { normalizePatientMatches } from '../types/patient-matching';

interface PatientDataJsonModalProps {
  open: boolean;
  onClose: () => void;
  patient: MatchedPatientData;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    style={{ height: '100%' }}
  >
    {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
  </div>
);

export const PatientDataJsonModal: React.FC<PatientDataJsonModalProps> = ({
  open,
  onClose,
  patient,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const matches = normalizePatientMatches(patient);

  // Generate combined/merged data preview
  const combinedData = useMemo(() => {
    const result: any = {
      _merge_metadata: {
        description: 'This is a preview of what the merged patient record would look like',
        extracted_patient_id: patient.extracted_patient_id,
        source_records: [] as string[],
        merge_strategy: 'Extracted data takes precedence, with matched data filling gaps',
      },
      ...patient.imported_patient_data,
    };

    // Add extracted patient ID to sources
    result._merge_metadata.source_records.push(`Extracted: ${patient.extracted_patient_id}`);

    // If there are matches, merge their data
    if (matches.length > 0) {
      matches.forEach((match) => {
        result._merge_metadata.source_records.push(`Matched: ${match.patient_id} (${match.confidence}% confidence)`);

        // Merge matched data - existing fields take precedence
        if (match.patient_data) {
          Object.keys(match.patient_data).forEach((key) => {
            if (result[key] === undefined || result[key] === null) {
              result[key] = match.patient_data?.[key];
            }
          });
        }
      });
    }

    return result;
  }, [patient, matches]);

  const handleCopy = async (data: any) => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      enqueueSnackbar('JSON copied to clipboard', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to copy JSON', { variant: 'error' });
    }
  };

  const getCurrentData = () => {
    if (tabValue === 0) return patient.imported_patient_data;
    if (tabValue === 1) {
      if (matches.length === 0) return null;
      if (matches.length === 1) return matches[0].patient_data;
      return matches.map(m => ({
        patient_id: m.patient_id,
        confidence: m.confidence,
        data: m.patient_data,
      }));
    }
    return combinedData;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
     maxWidth={"lg"} slotProps={{ paper: {
        sx: {
          height: '85vh',
          maxHeight: '85vh',
        }
      } }}>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h6">Patient Data JSON Viewer</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {patient.imported_patient_data?.first_name} {patient.imported_patient_data?.last_name}
              {patient.imported_patient_data?.date_of_birth && (
                <> • DOB: {patient.imported_patient_data.date_of_birth}</>
              )}
              {patient.imported_patient_data?.mrn && (
                <> • MRN: {patient.imported_patient_data.mrn}</>
              )}
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={() => handleCopy(getCurrentData())} size="small" sx={{ mr: 1 }}>
              <CopyIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tabValue} onChange={(_e, v: number) => setTabValue(v)}>
          <Tab label="Extracted Patient" />
          <Tab
            label={`Matched Patient${matches.length > 1 ? 's' : ''} (${matches.length})`}
            disabled={matches.length === 0}
          />
          <Tab label="Combined Preview" />
        </Tabs>
      </Box>

      <DialogContent
        dividers
        sx={{
          p: 0,
          bgcolor: '#1e1e1e',
          overflow: 'auto',
          flex: 1,
        }}
      >
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2, pb: 1 }}>
            <Alert severity="info" sx={{ bgcolor: '#2a3c54', color: '#d4d4d4' }}>
              <Typography variant="body2">
                <strong>Extracted Patient Data:</strong> This is the raw data extracted from the import file.
                Patient ID: {patient.extracted_patient_id}
              </Typography>
            </Alert>
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              pt: 0,
              fontSize: '13px',
              fontFamily: '"Fira Code", "Courier New", monospace',
              color: '#d4d4d4',
              lineHeight: 1.6,
              overflowX: 'auto',
              minHeight: '100%',
            }}
          >
            {JSON.stringify(patient.imported_patient_data, null, 2)}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {matches.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                No matched patients available
              </Alert>
            </Box>
          ) : matches.length === 1 ? (
            <>
              <Box sx={{ p: 2, pb: 1 }}>
                <Alert severity="warning" sx={{ bgcolor: '#4a3c2a', color: '#d4d4d4' }}>
                  <Typography variant="body2">
                    <strong>Matched Patient from Database:</strong> {matches[0].patient_data?.first_name} {matches[0].patient_data?.last_name}
                    {' • '} Patient ID: {matches[0].patient_id}
                    {' • '} {matches[0].confidence}% confidence match
                  </Typography>
                </Alert>
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  pt: 0,
                  fontSize: '13px',
                  fontFamily: '"Fira Code", "Courier New", monospace',
                  color: '#d4d4d4',
                  lineHeight: 1.6,
                  overflowX: 'auto',
                  minHeight: '100%',
                }}
              >
                {JSON.stringify(matches[0].patient_data, null, 2)}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ p: 2, pb: 1 }}>
                <Alert severity="warning" sx={{ bgcolor: '#4a3c2a', color: '#d4d4d4' }}>
                  <Typography variant="body2">
                    <strong>Multiple Matched Patients:</strong> Found {matches.length} potential matches in database.
                    Each match includes patient_id, confidence score, and full patient data.
                  </Typography>
                </Alert>
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  pt: 0,
                  fontSize: '13px',
                  fontFamily: '"Fira Code", "Courier New", monospace',
                  color: '#d4d4d4',
                  lineHeight: 1.6,
                  overflowX: 'auto',
                  minHeight: '100%',
                }}
              >
                {JSON.stringify(
                  matches.map(m => ({
                    patient_id: m.patient_id,
                    confidence: m.confidence,
                    data: m.patient_data,
                  })),
                  null,
                  2
                )}
              </Box>
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2, bgcolor: '#2a3c54', color: '#d4d4d4' }}>
              This preview shows how the merged patient record would look. Extracted data takes
              precedence, with matched patient data filling in any missing fields.
            </Alert>
          </Box>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              pt: 0,
              fontSize: '13px',
              fontFamily: '"Fira Code", "Courier New", monospace',
              color: '#d4d4d4',
              lineHeight: 1.6,
              overflowX: 'auto',
              minHeight: '100%',
            }}
          >
            {JSON.stringify(combinedData, null, 2)}
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => handleCopy(getCurrentData())}
          startIcon={<CopyIcon />}
          variant="outlined"
        >
          Copy Current Tab
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDataJsonModal;